/**
 * A control-rail button that toggles an associated popover panel.
 *
 * Centralizes the shared behavior for the Layers and Theme controls:
 * - ARIA wiring (`aria-haspopup`, `aria-expanded`)
 * - outside-click and Escape-to-close (Escape returns focus to the button)
 * - single-open coordination: opening one disclosure closes any others
 */
export interface Disclosure {
  button: HTMLButtonElement;
  panel: HTMLDivElement;
  setOpen: (open: boolean) => void;
  destroy: () => void;
}

export interface DisclosureOptions {
  /** Accessible name for the trigger button. */
  ariaLabel: string;
  /** Trusted, static icon markup for the button (never interpolated with user data). */
  svg: string;
  /** Class list for the panel element, e.g. `'panel panel--layers'`. */
  panelClass: string;
  /** `aria-haspopup` value; defaults to `'true'`. */
  haspopup?: string;
}

/** Every live disclosure registers a "close unless it's me" callback here. */
const registry = new Set<(keepOpen: HTMLElement) => void>();

export function createDisclosure(parent: HTMLElement, opts: DisclosureOptions): Disclosure {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'control-btn';
  button.setAttribute('aria-label', opts.ariaLabel);
  button.setAttribute('aria-haspopup', opts.haspopup ?? 'true');
  button.setAttribute('aria-expanded', 'false');
  button.innerHTML = opts.svg;

  const panel = document.createElement('div');
  panel.className = opts.panelClass;
  panel.hidden = true;

  const setOpen = (open: boolean): void => {
    if (open) for (const close of registry) close(panel); // close sibling panels first
    panel.hidden = !open;
    button.setAttribute('aria-expanded', String(open));
  };

  const closeUnless = (keepOpen: HTMLElement): void => {
    if (keepOpen !== panel && !panel.hidden) setOpen(false);
  };
  registry.add(closeUnless);

  const onButtonClick = (e: MouseEvent): void => {
    e.stopPropagation();
    setOpen(panel.hidden);
  };
  const onDocumentClick = (e: MouseEvent): void => {
    if (!panel.hidden && !panel.contains(e.target as Node) && e.target !== button) setOpen(false);
  };
  const onDocumentKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && !panel.hidden) {
      setOpen(false);
      button.focus();
    }
  };
  button.addEventListener('click', onButtonClick);
  document.addEventListener('click', onDocumentClick);
  document.addEventListener('keydown', onDocumentKeyDown);

  parent.appendChild(button);
  parent.appendChild(panel);
  let destroyed = false;
  return { button, panel, setOpen, destroy: () => {
    if (destroyed) return;
    destroyed = true;
    registry.delete(closeUnless);
    button.removeEventListener('click', onButtonClick);
    document.removeEventListener('click', onDocumentClick);
    document.removeEventListener('keydown', onDocumentKeyDown);
  } };
}
